<template>
  <div
    ref="carouselContainer"
    class="
      pre-header-banner
      critical-component-clear
      liquid
    "
    :class="`section-content--${preHeaderBannerTheme.content}`"
    role="complementary"
    :style="`--pre-header-background: ${preHeaderBannerTheme.background}`"
  >
    <div
      class="splide text-center"
      js-carousel="pre-header"
    >
      <div
        v-if="banners.length"
        class="splide__track"
      >
        <ul class="splide__list">
          <li
            v-for="(banner, index) of banners"
            :key="index"
            class="splide__slide"
            v-html="banner.message"
          />
        </ul>
      </div>

      <!-- Dismiss Button -->
      <div
        v-if="preHeaderBannerDismissable"
        class="pre-header-banner__action"
      >
        <button
          class="
          pre-header-banner__action-button
          pre-header-banner__action-button--icon
        "
          type="button"
          @click="handleDismissedBanner"
        >
          <icon-close />
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Component / Global: Site header/Pre-header banner
 * -----------------------------------------------------------------------------
 * Pre header announcement bar.
 * @param {Number} [autoplaySpeed] - Slide interval.
 * @param {Array} banners - Array of banner objects from section settings.
 * @param {Boolean} [preHeaderBannerDismissable] - Sets whether to make banner
 * able to be dismissed.
 * @param {String} preHeaderBannerId - ID for the pre-header banner.
 * @param {Object} [preHeaderBannerTheme] - Defines background and content for
 * pre-header banner component.
 * @param {Number} [preHeaderTransitionSpeed] - Banner transition speed.
 *
 */
import { nextTick } from 'vue'

import { mapActions, mapState } from 'vuex'

import Carousel from '~/api/carousel'

import IconClose from '~icons/misc/close.svg'

export default {
  name: 'PreHeaderBanner',

  components: {
    IconClose,
  },

  props: {
    autoplaySpeed: {
      type: Number,
      default: 3500,
      validator: (value) => {
        return typeof value === 'number'
      },
    },

    banners: {
      type: Array,
      default: () => [],
    },

    preHeaderBannerDismissable: {
      type: Boolean,
      default: false,
    },

    preHeaderBannerId: {
      type: String,
      default: '',
    },

    preHeaderBannerTheme: {
      type: Object,
      default: () => ({
        background: '',
        content: '',
      }),
    },

    preHeaderTransitionSpeed: {
      type: Number,
      default: 1,
    },
  },

  emits: {
    'announcement-height': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      selectors: {
        container: '[js-carousel="pre-header"]',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.createCarousel()
    this.checkAnnouncementHeight()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setBannerDismissed: 'header/setPreheaderDismissedStatus',
    }),

    /**
     * Checks the current height of announcement bar
     */
    checkAnnouncementHeight() {
      nextTick().then(() => {
        this.$emit('announcement-height', this.$refs.carouselContainer.offsetHeight)
        document.body.classList.add('pre-header-banner--visible')
      })
    },

    /**
     * Main carousel contructor function
     */
    createCarousel() {
      nextTick().then(() => {
        const container = this.$refs.carouselContainer

        if (!container) {
          return
        }
        const interval = Number(this.autoplaySpeed)
        const autoplay = Boolean(interval)

        Carousel(
          this.selectors.container,
          {
            type: 'fade',
            rewind: true,
            autoplay,
            interval,
          },
        ).mount()
      })
    },

    /**
     * Handles when pre-header-banner is closed.
     */
    handleDismissedBanner() {
      document.body.classList.remove('pre-header-banner--visible')
      document.body.classList.add('pre-header-banner--dismissed')

      // Convert speed to ms
      const transitionSpeed = Number(this.preHeaderTransitionSpeed) * 1000

      setTimeout(() => {
        this.setBannerDismissed(this.preHeaderBannerId)
        this.$emit('announcement-height', this.$refs.carouselContainer.offsetHeight)
      }, transitionSpeed)
    },
  },
}
</script>
